import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
    Lock, 
    ShieldAlert, 
    Plus, 
    Edit, 
    Trash2, 
    X, 
    AlertTriangle,
    CheckCircle2,
    Settings,
    LayoutDashboard
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import Swal from 'sweetalert2';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/shift-away.css';
import GlassCard from '../components/UI/GlassCard';

const Roles = () => {
    const { can } = useAuth();
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    
    const [formData, setFormData] = useState({
        id: null,
        nombre: '',
        descripcion: ''
    });

    const fetchRoles = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get('/backend/api/roles/index.php');
            if (response.data.success) {
                setRoles(response.data.data);
            }
        } catch (error) {
            toast.error('Error al sincronizar jerarquía');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRoles();
    }, [fetchRoles]);

    // Inicializar Tippy al terminar de cargar (React 19 safe)
    useEffect(() => {
        if (!loading) {
            tippy('[data-tippy-content]', {
                animation: 'shift-away',
                theme: 'translucent',
                arrow: true
            });
        }
    }, [roles, loading]);

    const handleOpenModal = (rol = null) => {
        if (rol) {
            setIsEditing(true);
            setFormData({
                id: rol.id,
                nombre: rol.nombre,
                descripcion: rol.descripcion || ''
            });
        } else {
            setIsEditing(false);
            setFormData({ id: null, nombre: '', descripcion: '' });
        }
        setShowModal(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post('/backend/api/roles/save.php', formData);
            if (response.data.success) {
                toast.success(response.data.message);
                setShowModal(false);
                fetchRoles();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Falla en el motor de roles');
        }
    };

    const handleDelete = async (id, nombre) => {
        const result = await Swal.fire({
            title: '¿Eliminar Rol?',
            text: `Ten cuidado. Solo se eliminará si no hay usuarios asignados a ${nombre}.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#1e293b',
            confirmButtonText: 'Sí, intentar borrar',
            background: '#0f172a',
            color: '#fff'
        });

        if (result.isConfirmed) {
            try {
                const response = await axios.post('/backend/api/roles/delete.php', { id });
                if (response.data.success) {
                    toast.success(response.data.message);
                    fetchRoles();
                } else {
                    toast.error(response.data.message);
                }
            } catch (error) {
                toast.error(error.response?.data?.message || 'Error crítico de integridad');
            }
        }
    };

    return (
        <div className="page-enter p-8 lg:p-12 max-w-[1400px] mx-auto min-h-screen animate-in fade-in duration-700">
            {/* Header Módulo */}
            <header className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
                <div>
                    <div className="inline-flex items-center gap-2 glass px-4 py-1.5 rounded-full border-white/5 mb-4 border-l-primary shadow-lg shadow-primary/10">
                        <Lock size={14} className="text-primary-light" />
                        <span className="text-[10px] font-black uppercase tracking-[2px] text-white/50">Matriz de Acceso</span>
                    </div>
                    <h1 className="text-5xl font-black text-white tracking-tighter">
                        Control de <span className="text-primary-light italic font-medium">Roles</span>
                    </h1>
                </div>
                <button 
                    onClick={() => handleOpenModal()}
                    className="group bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-2xl flex items-center gap-3 font-bold transition-all shadow-2xl shadow-primary/30 active:scale-95 border border-white/10"
                >
                    <Plus size={20} className="group-hover:rotate-90 transition-transform duration-500" />
                    Nuevo Rol de Poder
                </button>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Panel Informativo */}
                <div className="lg:col-span-1 space-y-6">
                    <GlassCard className="border-indigo-500/20 bg-indigo-500/5">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="p-3 rounded-2xl bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 shadow-lg">
                                <AlertTriangle size={24} />
                            </div>
                            <h3 className="text-xl font-black text-white italic tracking-tight leading-none">Aviso de Seguridad</h3>
                        </div>
                        <p className="text-white/40 text-sm font-medium leading-relaxed mb-6">
                            Los roles definen qué módulos puede ver y manipular el personal. Cambiar el nombre de un rol no afectará a los usuarios asignados, pero eliminarlo requerirá reasignar a todo el personal previa ejecución.
                        </p>
                        <div className="space-y-3">
                            <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 text-[10px] uppercase font-black tracking-widest text-white/30 border border-white/5">
                                <CheckCircle2 size={14} className="text-primary-light" />
                                Integridad Referencial Activa
                            </div>
                            <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 text-[10px] uppercase font-black tracking-widest text-white/30 border border-white/5">
                                <LayoutDashboard size={14} className="text-primary-light" />
                                Basado en RBAC SaaS v1.0
                            </div>
                        </div>
                    </GlassCard>
                </div>

                {/* Listado de Roles */}
                <div className="lg:col-span-2 space-y-6">
                    {loading ? (
                        <div className="h-64 flex flex-col items-center justify-center gap-4 glass rounded-[40px]">
                            <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
                            <p className="text-white/20 font-black uppercase tracking-widest text-[10px]">Descifrando Jerarquías...</p>
                        </div>
                    ) : (
                        roles.map((rol) => (
                            <GlassCard key={rol.id} className="group hover:bg-white/[0.03] border-white/5 transition-all">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-6">
                                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center font-black text-2xl shadow-xl transition-transform group-hover:scale-110 
                                            ${rol.id == 1 ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30' : 
                                              rol.id == 2 ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' : 
                                              'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'}
                                        `}>
                                            {rol.nombre[0]}
                                        </div>
                                        <div>
                                            <h4 className="text-2xl font-black text-white italic tracking-tighter leading-none mb-2">{rol.nombre}</h4>
                                            <p className="text-white/30 text-xs font-semibold leading-relaxed max-w-md">{rol.descripcion || 'Sin descripción detallada para este nivel de acceso.'}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        {can('Roles', 'editar') && (
                                            <button 
                                                data-tippy-content="Editar Rol"
                                                onClick={() => handleOpenModal(rol)}
                                                className="p-3 rounded-xl glass hover:bg-primary/20 hover:text-primary-light transition-all active:scale-90"
                                            >
                                                <Edit size={18} />
                                            </button>
                                        )}
                                        {can('Roles', 'eliminar') && (
                                            <button 
                                                data-tippy-content="Eliminar Jerarquía"
                                                onClick={() => handleDelete(rol.id, rol.nombre)}
                                                className="p-3 rounded-xl glass hover:bg-red-500/20 hover:text-red-400 transition-all active:scale-90"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </GlassCard>
                        ))
                    )}
                </div>
            </div>

            {/* Modal de Gestión de Rol */}
            {showModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 animate-in fade-in duration-300">
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={() => setShowModal(false)}></div>
                    <div className="relative w-full max-w-lg glass-heavy rounded-[40px] border border-white/10 shadow-2xl overflow-hidden animate-in slide-in-from-bottom-24 duration-500">
                        <div className="p-10 border-b border-white/5 bg-gradient-to-r from-primary/10 to-transparent">
                            <div className="flex items-center justify-between mb-2">
                                <h2 className="text-3xl font-black text-white italic tracking-tight">
                                    {isEditing ? 'Editar Jerarquía' : 'Nueva Jerarquía de Poder'}
                                </h2>
                                <button 
                                    onClick={() => setShowModal(false)}
                                    className="p-2 rounded-xl bg-white/5 text-white/40 hover:text-white"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                            <p className="text-white/40 text-[10px] font-black tracking-widest uppercase">Matriz de Acceso PawSpa Pro</p>
                        </div>
                        
                        <form onSubmit={handleSave} className="p-10 space-y-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-white/30 ml-4 italic">Nombre del Rol</label>
                                <div className="relative group">
                                    <Settings className="absolute left-6 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-primary-light" size={18} />
                                    <input 
                                        type="text" 
                                        required
                                        value={formData.nombre}
                                        onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                                        className="w-full bg-white/5 border border-white/5 rounded-2xl py-4 pl-16 pr-6 text-white outline-none focus:ring-2 focus:ring-primary/40 focus:bg-white/10 transition-all font-bold"
                                        placeholder="Ejem: Manager Global"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-white/30 ml-4 italic">Descripción de Atribuciones</label>
                                <textarea 
                                    value={formData.descripcion}
                                    onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                                    rows="4"
                                    className="w-full bg-white/5 border border-white/5 rounded-2xl py-4 px-6 text-white outline-none focus:ring-2 focus:ring-primary/40 focus:bg-white/10 transition-all font-bold resize-none"
                                    placeholder="Define qué puede hacer este perfil en el sistema..."
                                />
                            </div>

                            <button 
                                type="submit"
                                className="w-full bg-primary hover:bg-primary-dark text-white py-5 rounded-2xl font-black uppercase tracking-[3px] text-xs shadow-2xl shadow-primary/30 active:scale-95 transition-all mt-4"
                            >
                                {isEditing ? 'Confirmar Configuración' : 'Desplegar Nivel de Acceso'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Roles;
